import { useState, useEffect } from "react"

// ─── Constants (must match OverlayOptions.jsx) ────────────────────────────────
const OVERLAY_FONT_PX        = [18, 20, 22]   // question sizes
const OVERLAY_HEADER_FONT_PX = [20, 22, 24]   // heading sizes

const FONT_STEP_DEFAULT   = 1                          // Medium
const FONT_FAMILY_DEFAULT = '"Open Sans", sans-serif'

// ─── Hook: useOverlayStyle ────────────────────────────────────────────────────
// Pass the SAME storageKey you used in useOverlayOptions / OverlayProvider.
// The hook builds the localStorage keys dynamically from that prefix.
//
// Usage:
//   // Comprehension uses storageKey "comprehension"
//   const { cardHeaderStyle } = useOverlayStyle("comprehension")
//
//   // Preview page uses storageKey "preview"
//   const { cardHeaderStyle } = useOverlayStyle("preview")
//
//   // Default — falls back to "ov" (same default as useOverlayOptions)
//   const { cardHeaderStyle } = useOverlayStyle()

export function useOverlayStyle(storageKey = "ov") {

  // Build keys dynamically from the prefix — mirrors useOverlayOptions
  const KEY_FONTSTEP   = `${storageKey}_fontstep`
  const KEY_FONTFAMILY = `${storageKey}_fontfamily`

  // ── Readers ────────────────────────────────────────────────────────────────
  function readFontStep() {
    const v    = parseInt(localStorage.getItem(KEY_FONTSTEP), 10)
    const step = isNaN(v) ? FONT_STEP_DEFAULT : v
    return Math.min(OVERLAY_FONT_PX.length - 1, Math.max(0, step))
  }

  function readFontFamily() {
    return localStorage.getItem(KEY_FONTFAMILY) || FONT_FAMILY_DEFAULT
  }

  // ── State ──────────────────────────────────────────────────────────────────
  const [fontStep,   setFontStep]   = useState(readFontStep)
  const [fontFamily, setFontFamily] = useState(readFontFamily)

  // ── Listener ───────────────────────────────────────────────────────────────
  useEffect(() => {
    function handleChange() {
      setFontStep(readFontStep())
      setFontFamily(readFontFamily())
    }

    // Custom event fired by OverlayOptions whenever user changes a setting
    window.addEventListener("overlaychange", handleChange)

    // Native storage event — handles changes from other browser tabs
    window.addEventListener("storage", handleChange)

    return () => {
      window.removeEventListener("overlaychange", handleChange)
      window.removeEventListener("storage", handleChange)
    }
  }, [KEY_FONTSTEP, KEY_FONTFAMILY])   // re-register if storageKey prop changes

  // ── Styles ─────────────────────────────────────────────────────────────────
  const cardStyle = {
    position:   "relative",
    fontSize:   OVERLAY_FONT_PX[fontStep] + "px",
    fontFamily: fontFamily,
  }

  const cardHeaderStyle = {
    position:   "relative",
    fontSize:   OVERLAY_HEADER_FONT_PX[fontStep] + "px",
    fontFamily: fontFamily,
  }

  return {
    cardStyle,
    cardHeaderStyle,
    fontStep,
    fontFamily,
    currentQuestionPx: OVERLAY_FONT_PX[fontStep],
    currentHeadingPx:  OVERLAY_HEADER_FONT_PX[fontStep],
  }
}